import {computed, ref, watch, nextTick} from 'vue';
import {PulseTimeAllocation} from '../types/DataTypes.ts';
import {useTimeAllocationCategoriesStore} from '../stores/timeAllocationCategories.store.ts';

export function useTimeAllocation(
  modelValue: PulseTimeAllocation | null,
  emit: (event: 'update:modelValue', value: PulseTimeAllocation | null) => void
) {
  const categoriesStore = useTimeAllocationCategoriesStore();
  
  // Initialize allocation with all zeros
  const createEmptyAllocation = (): PulseTimeAllocation => {
    const allocation: any = {};
    // Only create allocation if categories are loaded
    if (categoriesStore.active.length > 0) {
      categoriesStore.active.forEach(cat => {
        allocation[cat.key] = 0;
      });
    }
    return allocation;
  };

  const localAllocation = ref<PulseTimeAllocation>(
    modelValue || createEmptyAllocation()
  );

  // Flag to prevent recursive updates
  const isUpdatingFromProps = ref(false);

  // Computed properties for allocation analysis
  const enabledCategories = computed(() => {
    return categoriesStore.active.filter(category => 
      (localAllocation.value[category.key as keyof PulseTimeAllocation] || 0) > 0
    );
  });

  const total = computed(() => {
    return Object.values(localAllocation.value).reduce((sum: number, val: number) => sum + val, 0);
  });

  const isValid = computed(() => {
    return total.value === 100 && Object.values(localAllocation.value).some(val => val > 0);
  });



  // Watch for changes and emit updates
  watch(localAllocation, (newVal) => {
    if (!isUpdatingFromProps.value) {
      emit('update:modelValue', newVal);
    }
  }, {deep: true});

  watch(() => modelValue, (newVal) => {
    isUpdatingFromProps.value = true;
    if (newVal) {
      localAllocation.value = {...newVal};
    } else {
      localAllocation.value = createEmptyAllocation();
    }
    nextTick(() => {
      isUpdatingFromProps.value = false;
    });
  });

  // Core allocation functions
  function enableCategory(key: string) {
    const enabled = enabledCategories.value;
    
    if (enabled.length === 0) {
      // First category gets 100%
      localAllocation.value[key as keyof PulseTimeAllocation] = 100;
    } else {
      // Find the rightmost category with >5% allocation
      let donorCategory = null;
      let donorValue = 0;
      
      for (let i = enabled.length - 1; i >= 0; i--) {
        const categoryValue = localAllocation.value[enabled[i].key as keyof PulseTimeAllocation] || 0;
        if (categoryValue > 5) {
          donorCategory = enabled[i];
          donorValue = categoryValue;
          break;
        }
      }
      
      if (donorCategory) {
        // Take half of the donor category's allocation (rounded to nearest 5, min 5)
        const halfValue = Math.max(5, Math.floor(donorValue / 2 / 5) * 5);
        localAllocation.value[donorCategory.key as keyof PulseTimeAllocation] = donorValue - halfValue;
        localAllocation.value[key as keyof PulseTimeAllocation] = halfValue;
      } else {
        redistributeForNewCategory(key, enabled);
      }
    }
  }

  function redistributeForNewCategory(key: string, enabled: any[]) {
    // If no category has >5%, give the new category 5% and adjust others proportionally
    const remaining = 95;
    
    // Redistribute the remaining 95% among existing categories
    enabled.forEach(category => {
      const currentValue = localAllocation.value[category.key as keyof PulseTimeAllocation] || 0;
      const newValue = Math.floor((currentValue / total.value) * remaining / 5) * 5;
      localAllocation.value[category.key as keyof PulseTimeAllocation] = Math.max(5, newValue);
    });
    
    localAllocation.value[key as keyof PulseTimeAllocation] = 5;
    
    // Ensure total is exactly 100%
    const newTotal = Object.values(localAllocation.value).reduce((sum, val) => sum + val, 0);
    if (newTotal !== 100) {
      const firstEnabled = enabled[0];
      const adjustment = 100 - newTotal;
      localAllocation.value[firstEnabled.key as keyof PulseTimeAllocation] += adjustment;
    }
  }

  function disableCategory(key: string) {
    const currentValue = localAllocation.value[key as keyof PulseTimeAllocation] || 0;
    const enabled = enabledCategories.value;
    
    if (currentValue > 0 && enabled.length > 1) {
      // Redistribute the value to the rightmost remaining category
      const remainingCategories = enabled.filter(cat => cat.key !== key);
      if (remainingCategories.length > 0) {
        const rightmostCategory = remainingCategories[remainingCategories.length - 1];
        localAllocation.value[rightmostCategory.key as keyof PulseTimeAllocation] += currentValue;
      }
    }
    
    localAllocation.value[key as keyof PulseTimeAllocation] = 0;
  }

  function adjustCategoryPercentage(categoryKey: string, direction: 'increase' | 'decrease') {
    const enabled = enabledCategories.value;
    const currentValue = localAllocation.value[categoryKey as keyof PulseTimeAllocation] || 0;
    const step = 5; // Move by 5% increments
    
    if (direction === 'decrease' && currentValue <= step) return;
    if (direction === 'increase' && currentValue >= 95) return;
    
    // Find another category to adjust in opposite direction
    const targetCategory = findAdjustmentTarget(enabled, categoryKey, direction, step);
    if (!targetCategory) return;
    
    // Apply the adjustment
    const newCurrentValue = direction === 'increase' ? currentValue + step : currentValue - step;
    const targetValue = localAllocation.value[targetCategory.key as keyof PulseTimeAllocation] || 0;
    const newTargetValue = direction === 'increase' ? targetValue - step : targetValue + step;
    
    localAllocation.value[categoryKey as keyof PulseTimeAllocation] = newCurrentValue;
    localAllocation.value[targetCategory.key as keyof PulseTimeAllocation] = newTargetValue;
  }

  function findAdjustmentTarget(enabled: any[], categoryKey: string, direction: 'increase' | 'decrease', step: number) {
    if (direction === 'increase') {
      // Find a category that can be decreased (has more than 5%)
      return enabled.find(category => {
        if (category.key === categoryKey) return false;
        const otherValue = localAllocation.value[category.key as keyof PulseTimeAllocation] || 0;
        return otherValue > step;
      });
    } else {
      // Find a category that can be increased (has less than 95%)
      return enabled.find(category => {
        if (category.key === categoryKey) return false;
        const otherValue = localAllocation.value[category.key as keyof PulseTimeAllocation] || 0;
        return otherValue < 95;
      });
    }
  }

  function canDecrease(categoryKey: string): boolean {
    const currentValue = localAllocation.value[categoryKey as keyof PulseTimeAllocation] || 0;
    return currentValue > 5;
  }

  function canIncrease(categoryKey: string): boolean {
    const currentValue = localAllocation.value[categoryKey as keyof PulseTimeAllocation] || 0;
    if (currentValue >= 95) return false;
    
    // Check if there's another category that can be decreased
    return enabledCategories.value.some(category => {
      if (category.key === categoryKey) return false;
      const otherValue = localAllocation.value[category.key as keyof PulseTimeAllocation] || 0;
      return otherValue > 5;
    });
  }

  return {
    localAllocation,
    enabledCategories,
    total,
    isValid,
    enableCategory,
    disableCategory,
    adjustCategoryPercentage,
    canDecrease,
    canIncrease
  };
}
